/**
 * Shared API client for every front end (admin, driver PWA, future customer web / mobile).
 * - The session (access token in memory, refresh cookie per app, refresh across tabs) lives in
 *   `SessionManager` (./session.ts); this client only sends requests through it.
 * - Every request carries `X-Requested-With: despachador` and `X-App: admin | driver`.
 * - One realtime socket with first-message auth, heartbeat and exponential reconnect.
 */
import type { SecurityEvent, SecurityEventPage, SecurityEventResult, SecurityEventType } from '@despachador/contracts/auth-errors';
import { SessionManager, signedOutReason, type AppName } from './session';

export * from './session';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
    readonly requestId?: string,
  ) {
    super(message);
  }
}

/** Status 0: the request never reached the server (offline, DNS, CORS, server down). */
export const NETWORK_ERROR = 'NETWORK_ERROR';

/** The account as a sign-in answers it. */
export interface SignedInUser {
  id: string;
  fullName: string;
  email: string | null;
  phone: string;
}

/** Second factor setup: the secret to type and the link the QR code carries. */
export interface MfaSetup {
  secret: string;
  otpauthUrl: string;
}

/** What `/auth/login` and `/auth/login/step` answer (200): a session, or the next step. */
export type SignInAnswer =
  | {
      status: 'SIGNED_IN';
      accessToken: string;
      expiresIn: number;
      audience: AppName;
      user: SignedInUser;
      /** Only right after the second factor was set up during this sign-in: shown once. */
      recoveryCodes?: string[];
      /** An activation that linked a Google account on the way (E3). */
      googleLinked?: boolean;
    }
  | {
      status: 'CHALLENGE';
      step: 'MFA' | 'MFA_SETUP';
      code: 'MFA_REQUIRED' | 'MFA_SETUP_REQUIRED';
      challenge: string;
      expiresIn: number;
      audience: AppName;
      methods?: string[];
      setup?: MfaSetup;
    };

export interface SignInInput {
  identifier: string;
  password: string;
  /** Company slug: development only (in production the host names it and the API refuses it). */
  tenant?: string;
  deviceKey?: string;
  deviceLabel?: string;
  platform?: 'WEB' | 'PWA';
}

export type SignInStepInput = { challenge: string } & ({ code: string } | { recoveryCode: string } | { action: 'start' } | { action: 'confirm'; code: string });

export type SignedIn = Extract<SignInAnswer, { status: 'SIGNED_IN' }>;

// ---------------------------------------------------------------- invitations and activation (E2)
/** The link of an invitation: answered once by the route that created or renewed it, never listed. */
export interface Invitation {
  /** `https://<app>/acceso/activar#t=<token>`. */
  link: string;
  expiresAt: string;
  /** App the link opens. */
  app: AppName;
}

export type AccountStatus = 'INVITED' | 'ACTIVE' | 'SUSPENDED' | 'DISABLED';

/** A member as `GET /admin/users` lists it. */
export interface MemberRow {
  id: string;
  email: string | null;
  phone: string;
  full_name: string;
  status: AccountStatus;
  status_reason: string | null;
  mfa_enabled: boolean;
  mfa_required: boolean;
  phone_verified: boolean;
  roles: string[];
  driver_id: string | null;
  last_login_at: string | null;
  /** ACTIVE, the policy demands a second factor and there is none: needs an invitation to set it up. */
  mfa_setup_pending: boolean;
  /** Placeholder number: no invitation or code can be sent until it is corrected. */
  phone_requires_update: boolean;
  /** Expiry of the pending invitation, or null. */
  invitation_expires_at: string | null;
  phone_changed_at: string | null;
  /** Who changed the number last (when the API tells it). */
  phone_changed_by_name?: string | null;
  /** A Google account opens this account (E3). */
  google_linked?: boolean;
}

export interface NewMemberInput {
  fullName: string;
  phone: string;
  email?: string;
  roleIds: string[];
}

export interface NewDriverInput {
  fullName: string;
  phone: string;
  email?: string;
  code?: string;
  licenseNumber?: string;
  vehicleId?: string;
  priority?: number;
}

/** What `POST /auth/invitations/inspect` tells about an invitation before any code is sent. */
export interface InvitationInfo {
  brandName: string;
  fullName: string;
  phoneMasked: string;
  steps: string[];
  expiresAt: string;
}

/** What the activation will ask about the second factor, told with the ticket. */
export type ActivationMfa = 'NONE' | 'SETUP' | 'VERIFY';

export interface ActivationCompleteInput {
  activationTicket: string;
  password: string;
  code?: string;
  recoveryCode?: string;
  /** Ticket of the Google account chosen during the activation (`#google=`): linked when it completes. */
  googleTicket?: string;
  deviceKey?: string;
  deviceLabel?: string;
  platform?: 'WEB' | 'PWA';
}

// ---------------------------------------------------------------- E3: recovery, own credentials, Google
/** Re-authentication: one first factor (password or the ticket of a fresh Google sign-in) and, with 2FA, the second. */
export type ReauthInput = ({ password: string } | { externalTicket: string }) & { code?: string; recoveryCode?: string };

/** `GET /auth/external/google`: the caller's own Google account. */
export interface GoogleAccountStatus {
  /** The provider is configured and the company switched sign-in with Google on. */
  available: boolean;
  linked: boolean;
  /** Email of the Google account when it was linked (only its holder sees it). */
  email: string | null;
  linkedAt: string | null;
  hasPassword: boolean;
}

/** `GET /auth/mfa`. */
export interface MfaStatus {
  enabled: boolean;
  required: boolean;
  reason?: string | null;
  recoveryCodesRemaining: number;
}

/** A code sent to the WhatsApp of the account. */
export interface CodeSent {
  sent: boolean;
  phoneMasked: string;
  expiresIn: number;
}

/** A notification of the inbox (`GET /me/notifications`). SECURITY ones carry `data.kind` (never a secret). */
export interface InboxNotification {
  id: string;
  category: string;
  title: string;
  body: string | null;
  data: (Record<string, unknown> & { kind?: string }) | null;
  priority: string;
  read_at: string | null;
  created_at: string;
}

/** `GET /public/branding`: what the screens without a session know about this address. */
export interface PublicBranding {
  found: boolean;
  branding?: Record<string, any> & { brandName: string; tenantSlug: string; logoUrl?: string | null };
  features?: Record<string, boolean>;
  /** Sign-in with Google is configured and switched on: the only thing that shows its button. */
  googleSignIn?: boolean;
}

/** `POST /auth/invitations/complete`: the session, or the second-factor step to answer with the same call. */
export type ActivationAnswer = SignedIn | { status: 'MFA_SETUP_REQUIRED'; setup: MfaSetup } | { status: 'MFA_REQUIRED'; methods: string[] };

/** Filters of `GET /admin/security/events` (docs/authentication.md «Eventos de seguridad»). */
export interface SecurityEventsQuery {
  /** Event types of the catalog; none = every type. */
  type?: readonly SecurityEventType[];
  /** The person the events are about. */
  subjectUserId?: string;
  /** Who did it. */
  actorId?: string;
  result?: SecurityEventResult;
  /** ISO 8601 with zone; `from` inclusive, `to` exclusive. */
  from?: string;
  to?: string;
  /** `nextCursor` of the previous page. */
  cursor?: string;
  /** 1 to 100 (50). */
  limit?: number;
}

type Listener = (msg: any) => void;

/** Requests that are part of signing in or out: their 401 is an answer, never a reason to refresh. */
const NO_REFRESH = new Set([
  '/auth/login',
  '/auth/login/step',
  '/auth/login/external',
  '/auth/refresh',
  '/auth/logout',
  '/auth/invitations/inspect',
  '/auth/invitations/otp',
  '/auth/invitations/verify',
  '/auth/invitations/complete',
]);

export class ApiClient {
  readonly session: SessionManager;

  constructor(
    readonly baseUrl: string,
    opts: { app: AppName },
  ) {
    this.session = new SessionManager(baseUrl, opts.app);
  }

  get app(): AppName {
    return this.session.app;
  }

  private async raw(method: string, path: string, body?: unknown, headers: Record<string, string> = {}, signal?: AbortSignal): Promise<Response> {
    const binary = body instanceof Blob;
    const h: Record<string, string> = { ...this.session.headers(), ...headers };
    if (body !== undefined && !binary) h['Content-Type'] = 'application/json';
    const token = this.session.token;
    if (token && path !== '/auth/logout') h.Authorization = `Bearer ${token}`;
    try {
      return await fetch(`${this.baseUrl}${path}`, {
        method,
        headers: h,
        body: body === undefined ? undefined : binary ? body : JSON.stringify(body),
        credentials: 'include',
        signal,
      });
    } catch (err) {
      // a cancelled request (superseded search) is not a connectivity problem
      if (signal?.aborted) throw err;
      throw new ApiError(0, NETWORK_ERROR, 'No hay conexión con el servidor');
    }
  }

  /**
   * Sends a request on behalf of the session: a 401 UNAUTHORIZED (expired access token) refreshes
   * once and retries; a 401 saying the session is gone ends it. A failed refresh because the API is
   * unreachable (network, 429, 5xx) keeps the session and fails this request only.
   */
  private async send(method: string, path: string, body?: unknown, headers?: Record<string, string>, signal?: AbortSignal): Promise<Response> {
    const bare = path.split('?')[0]!;
    const res = await this.raw(method, path, body, headers, signal);
    if (res.status !== 401 || NO_REFRESH.has(bare)) return res;
    const err = (await res
      .clone()
      .json()
      .catch(() => ({}))) as { code?: string; details?: unknown };
    if (err.code === 'SESSION_EXPIRED' || err.code === 'SESSION_REVOKED') {
      if (this.session.state.status === 'signed-in') this.session.end(signedOutReason(err.code, err.details, 'running'));
      return res;
    }
    if (err.code !== 'UNAUTHORIZED' || this.session.state.status !== 'signed-in') return res;
    const outcome = await this.session.refresh();
    if (outcome === 'ok') return this.raw(method, path, body, headers, signal);
    if (outcome === 'unavailable') {
      const f = this.session.lastFailure;
      throw f && f.status !== 0 ? new ApiError(f.status, f.code, f.message, f.details) : new ApiError(0, NETWORK_ERROR, 'No hay conexión con el servidor');
    }
    return res;
  }

  async request<T = any>(method: string, path: string, body?: unknown, headers?: Record<string, string>, signal?: AbortSignal): Promise<T> {
    const res = await this.send(method, path, body, headers, signal);
    if (!res.ok) {
      const err = (await res.json().catch(() => ({}))) as { code?: string; message?: string; details?: unknown; requestId?: string };
      throw new ApiError(res.status, err.code ?? 'ERROR', err.message ?? res.statusText, err.details, err.requestId ?? res.headers.get('x-request-id') ?? undefined);
    }
    if (res.status === 204) return undefined as T;
    const type = res.headers.get('content-type') ?? '';
    return (type.includes('application/json') ? res.json() : res.text()) as Promise<T>;
  }

  get = <T = any>(path: string, opts: { signal?: AbortSignal } = {}) => this.request<T>('GET', path, undefined, undefined, opts.signal);
  post = <T = any>(path: string, body?: unknown, headers?: Record<string, string>) => this.request<T>('POST', path, body ?? {}, headers);
  put = <T = any>(path: string, body?: unknown) => this.request<T>('PUT', path, body ?? {});
  patch = <T = any>(path: string, body?: unknown) => this.request<T>('PATCH', path, body ?? {});
  del = <T = any>(path: string, body?: unknown) => this.request<T>('DELETE', path, body);
  /** Sends a file as the raw request body (imports). The content type is the file's, or derived from its extension. */
  upload = <T = any>(path: string, file: Blob, contentType?: string) =>
    this.request<T>('POST', path, file, { 'Content-Type': contentType || file.type || 'application/octet-stream' });

  // ---------------------------------------------------------------- sign in and out
  /**
   * Password sign-in. Answers the session or the next step; it does not start the session: the
   * caller does with `session.establish(answer)` once the person has seen what comes with it
   * (recovery codes after a mandatory second-factor setup).
   */
  login = (input: SignInInput) => this.request<SignInAnswer>('POST', '/auth/login', input);
  /** Next step of a sign-in (second factor, its mandatory setup). Same answers as `login`. */
  loginStep = (input: SignInStepInput) => this.request<SignInAnswer>('POST', '/auth/login/step', input);
  /**
   * Redeems the ticket Google's return brought (`/acceso/google#ticket=`): the same answers as
   * `login` (a session, or the second-factor step). The ticket is bound to this browser by a cookie.
   */
  loginExternal = (input: { ticket: string; deviceKey?: string; deviceLabel?: string; platform?: 'WEB' | 'PWA' }) => this.request<SignInAnswer>('POST', '/auth/login/external', input);
  /**
   * Where the "Continuar con Google" button sends the browser (a full page navigation, never fetch).
   * `tenant` only where the address does not name the company (development); production refuses it.
   */
  googleStartUrl = (tenant?: string | null) => `${this.baseUrl}/auth/oidc/google/start?app=${this.app}${tenant ? `&tenant=${encodeURIComponent(tenant)}` : ''}`;
  /** Signs out of this session; the local session ends even if the server cannot be reached. */
  logout = () => this.session.signOut();
  /** Ends every session of the account, this one included. */
  async logoutAll(): Promise<void> {
    await this.request('POST', '/auth/logout-all', {});
    this.session.end('none');
  }

  // ---------------------------------------------------------------- activation of an invitation (public)
  /**
   * The four steps of `/acceso/activar` (docs/authentication.md). They are sent without the bearer
   * of whatever session the browser holds: the invitation token is the only credential.
   */
  private anonymous = async <T>(path: string, body: unknown): Promise<T> => {
    let res: Response;
    try {
      res = await fetch(`${this.baseUrl}${path}`, { method: 'POST', credentials: 'include', headers: { ...this.session.headers(), 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    } catch {
      throw new ApiError(0, NETWORK_ERROR, 'No hay conexión con el servidor');
    }
    const data = (await res.json().catch(() => ({}))) as any;
    if (!res.ok) throw new ApiError(res.status, data.code ?? 'ERROR', data.message ?? res.statusText, data.details, data.requestId ?? res.headers.get('x-request-id') ?? undefined);
    return data as T;
  };
  activation = {
    inspect: (token: string) => this.anonymous<InvitationInfo>('/auth/invitations/inspect', { token }),
    /** Sends (or sends again) the 6-digit code to the invited phone; a new code replaces the previous one. */
    sendCode: (token: string) => this.anonymous<{ sent: boolean; phoneMasked: string; expiresIn: number }>('/auth/invitations/otp', { token }),
    verifyCode: (token: string, code: string) => this.anonymous<{ activationTicket: string; expiresIn: number; mfa: ActivationMfa }>('/auth/invitations/verify', { token, code }),
    /** Does not start the session: the caller does with `session.establish(answer)` (like `login`). */
    complete: (input: ActivationCompleteInput) => this.anonymous<ActivationAnswer>('/auth/invitations/complete', input),
    /** Optional, after the code: where to send the browser to choose the Google account to link. */
    googleStart: (activationTicket: string) => this.anonymous<{ url: string }>('/auth/invitations/google', { activationTicket }),
    /** Which Google account came back (`#google=`), to show it before completing. */
    googleInspect: (activationTicket: string, googleTicket: string) => this.anonymous<{ email: string | null }>('/auth/invitations/google/inspect', { activationTicket, googleTicket }),
  };

  // ---------------------------------------------------------------- password recovery (public, E3)
  /**
   * `/acceso/recuperar`: no session and no bearer. The first step answers the same for any
   * identifier (it never tells whether an account exists); the last one does not sign in.
   */
  recovery = {
    start: (identifier: string, tenant?: string | null) => this.anonymous<{ challengeId: string; expiresIn: number }>('/auth/recovery', { identifier, ...(tenant ? { tenant } : {}) }),
    verify: (challengeId: string, code: string) => this.anonymous<{ grant: string; expiresIn: number }>('/auth/recovery/verify', { challengeId, code }),
    complete: (grant: string, password: string) => this.anonymous<{ status: 'PASSWORD_RESET' }>('/auth/recovery/complete', { grant, password }),
  };

  /** Own inbox (signed in). */
  notifications = {
    inbox: (limit = 30) => this.get<InboxNotification[]>(`/me/notifications?limit=${limit}`),
  };

  // ---------------------------------------------------------------- own account (signed in, E3)
  /** Operations marked "reauth" answer 403 REAUTH_REQUIRED without a recent one: wrap them with `useReauth`. */
  account = {
    reauth: (input: ReauthInput) => this.post<{ authenticatedAt: string }>('/auth/reauth', input),
    /** Reauth. The other sessions end; this one keeps going (new refresh cookie). */
    changePassword: (currentPassword: string, newPassword: string) => this.post<{ revokedSessions: number }>('/auth/password', { currentPassword, newPassword }),
    mfa: {
      status: () => this.get<MfaStatus>('/auth/mfa'),
      /** Reauth. Step 1 of the voluntary setup: the secret and the QR. */
      enroll: () => this.post<MfaSetup>('/auth/mfa/totp/enroll', {}),
      /** Reauth. Step 2: a code to the verified WhatsApp of the account (a new one replaces the previous). */
      sendCode: () => this.post<CodeSent>('/auth/mfa/totp/otp', {}),
      /** Reauth. Step 3: the app's code and the WhatsApp code. Answers the recovery codes, once. */
      confirm: (code: string, otp: string) => this.post<{ recoveryCodes: string[] }>('/auth/mfa/totp/confirm', { code, otp }),
      disable: () => this.post<void>('/auth/mfa/totp/disable', {}),
      recoveryCodes: () => this.post<{ recoveryCodes: string[] }>('/auth/mfa/recovery-codes', {}),
    },
    google: {
      status: () => this.get<GoogleAccountStatus>('/auth/external/google'),
      /** Reauth. Where to send the browser; it comes back with `#link=`. */
      link: () => this.post<{ url: string }>('/auth/external/google/link', {}),
      inspect: (ticket: string) => this.post<{ email: string | null }>('/auth/external/google/link/inspect', { ticket }),
      /** Reauth. */
      confirm: (ticket: string) => this.post<{ linked: true; email: string | null }>('/auth/external/google/link/confirm', { ticket }),
      /** A fresh Google sign-in to answer REAUTH_REQUIRED; it comes back with `#reauth=`. */
      reauth: () => this.post<{ url: string }>('/auth/external/google/reauth', {}),
      /** Reauth. Ends the sessions opened with Google (this one too, if it is one). */
      unlink: () => this.del<{ revokedSessions: number }>('/auth/external/google'),
    },
  };

  // ---------------------------------------------------------------- members: invitations, phone, second factor
  members = {
    list: (limit = 200) => this.get<MemberRow[]>(`/admin/users?limit=${limit}`),
    /** People whose name matches (`users.view`). */
    search: (search: string, limit = 8) => this.get<MemberRow[]>(`/admin/users?limit=${limit}&search=${encodeURIComponent(search)}`),
    /** Creates the INVITED account; `invitation` comes back once. */
    invite: (input: NewMemberInput) => this.post<{ id: string; full_name: string; phone: string; status: AccountStatus; invitation: Invitation }>('/admin/users', input),
    /** New link (the previous one stops working). DISABLED accounts come back to INVITED. 3 a day per account. */
    resendInvitation: (userId: string) => this.post<{ status: AccountStatus; invitation: Invitation }>(`/admin/users/${userId}/invitation/resend`, {}),
    cancelInvitation: (userId: string) => this.del<{ status: AccountStatus }>(`/admin/users/${userId}/invitation`),
    /** Needs a recent re-authentication (wrap with `useReauth`). The account goes back to INVITED. */
    changePhone: (userId: string, phone: string) => this.post<{ status: AccountStatus; phone: string; invitation: Invitation }>(`/admin/users/${userId}/phone`, { phone }),
    /** Needs a recent re-authentication. `invitation` only for an INVITED account. */
    resetMfa: (userId: string) => this.post<{ status: AccountStatus; mfa_setup_pending: boolean; invitation: Invitation | null }>(`/admin/users/${userId}/mfa/reset`, {}),
    /** Needs a recent re-authentication. Ends the sessions opened with Google; the reason stays in the audit. */
    unlinkGoogle: (userId: string, reason: string) => this.post<{ unlinked: true; revoked: number }>(`/admin/users/${userId}/google/unlink`, { reason }),
  };

  drivers = {
    /** Creates the driver and its INVITED app account; `invitation` (driver app) comes back once. */
    create: (input: NewDriverInput) =>
      this.post<{ id: string; full_name: string; account: { userId: string; status: AccountStatus; phone: string; email: string | null }; invitation: Invitation } & Record<string, any>>('/admin/drivers', input),
    /** `invitation` only when reactivating brought back an account whose invitation had been cancelled. */
    setStatus: (driverId: string, status: 'ACTIVE' | 'SUSPENDED' | 'INACTIVE', reason?: string) =>
      this.put<{ user_status: AccountStatus; invitation?: Invitation } & Record<string, any>>(`/admin/drivers/${driverId}/status`, { status, ...(reason ? { reason } : {}) }),
  };

  // ---------------------------------------------------------------- security events (E4)
  security = {
    /** A page of the company's security events, newest first (`audit.view`). Read-only. */
    events: (q: SecurityEventsQuery = {}) => {
      const p = new URLSearchParams();
      if (q.type?.length) p.set('type', q.type.join(','));
      if (q.subjectUserId) p.set('subjectUserId', q.subjectUserId);
      if (q.actorId) p.set('actorId', q.actorId);
      if (q.result) p.set('result', q.result);
      if (q.from) p.set('from', q.from);
      if (q.to) p.set('to', q.to);
      if (q.cursor) p.set('cursor', q.cursor);
      if (q.limit) p.set('limit', String(q.limit));
      const qs = p.toString();
      return this.get<SecurityEventPage<SecurityEvent>>(`/admin/security/events${qs ? `?${qs}` : ''}`);
    },
  };

  /** Fetches a private binary (a document's file) with auth, to show it through an object URL. */
  async blob(path: string): Promise<Blob> {
    const res = await this.send('GET', path);
    if (!res.ok) {
      const err = (await res.json().catch(() => ({}))) as { code?: string; message?: string; requestId?: string };
      throw new ApiError(res.status, err.code ?? 'ERROR', err.message ?? res.statusText, undefined, err.requestId ?? res.headers.get('x-request-id') ?? undefined);
    }
    return res.blob();
  }

  /** Downloads a binary export with auth. */
  async download(path: string, fileName: string) {
    const res = await this.send('GET', path);
    if (!res.ok) throw new ApiError(res.status, 'DOWNLOAD_FAILED', 'No se pudo descargar');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

export type SocketState = 'connecting' | 'open' | 'closed';

/** Close code of the API when the socket's session is not (or no longer) valid. */
const WS_UNAUTHORIZED = 4401;

export class RealtimeSocket {
  private ws: WebSocket | null = null;
  private listeners = new Map<string, Set<Listener>>();
  private stateListeners = new Set<(s: SocketState) => void>();
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private retry = 0;
  private stopped = false;
  private opening = false;
  private queue: object[] = [];
  state: SocketState = 'closed';

  constructor(
    private readonly url: string,
    private readonly session: SessionManager,
    private readonly heartbeatMs = 15_000,
  ) {
    // a session that ends closes the socket; the app connects again after the next sign-in
    session.subscribe((s) => {
      if (s.status === 'signed-out') this.close();
    });
  }

  connect() {
    this.stopped = false;
    if (this.ws) return; // already connected / connecting
    void this.open();
  }

  private setState(s: SocketState) {
    this.state = s;
    this.stateListeners.forEach((l) => l(s));
  }

  private async open() {
    if (this.stopped || this.ws || this.opening) return;
    this.setState('connecting');
    this.opening = true;
    let token: string | null;
    try {
      token = await this.session.freshToken();
    } finally {
      this.opening = false;
    }
    if (this.stopped) return;
    if (!token) {
      this.setState('closed');
      // no session: stay closed; API unreachable: try again like any other drop
      if (this.session.state.status !== 'signed-out') this.reconnectLater();
      return;
    }
    if (this.ws) return;
    const ws = new WebSocket(this.url);
    this.ws = ws;
    ws.onopen = () => ws.send(JSON.stringify({ type: 'auth', token }));
    ws.onmessage = (ev) => {
      const msg = JSON.parse(String(ev.data));
      if (msg.type === 'auth.ok') {
        this.retry = 0;
        this.setState('open');
        this.heartbeat = setInterval(() => this.send({ type: 'ping' }), this.heartbeatMs);
        const pending = this.queue.splice(0);
        pending.forEach((m) => this.send(m));
      }
      this.emit(msg.type, msg);
      this.emit('*', msg);
    };
    ws.onclose = (ev) => {
      if (this.ws !== ws) return; // a stale socket closing after a reconnect
      if (this.heartbeat) clearInterval(this.heartbeat);
      this.heartbeat = null;
      this.ws = null;
      this.setState('closed');
      if (this.stopped) return;
      if (ev.code === WS_UNAUTHORIZED) return void this.reauthorize();
      this.reconnectLater();
    };
    ws.onerror = () => ws.close();
  }

  /** 4401: the token expired or the session was revoked. A refresh tells which one. */
  private async reauthorize() {
    const outcome = await this.session.refresh();
    if (this.stopped) return;
    if (outcome === 'ok') return void this.open();
    if (outcome === 'dead') {
      // the refresh already ended the session; make sure it says why it ended
      if (this.session.state.status !== 'signed-out') this.session.end('revoked');
      return;
    }
    this.reconnectLater();
  }

  private reconnectLater() {
    const delay = Math.min(30_000, 1000 * 2 ** this.retry++) + Math.random() * 500;
    setTimeout(() => void this.open(), delay);
  }

  send(msg: object) {
    if (this.ws && this.state === 'open') this.ws.send(JSON.stringify(msg));
    else if ((msg as { type?: string }).type !== 'ping') this.queue.push(msg);
  }

  /**
   * Sends only if connected right now; never queued. For time-sensitive messages that would be
   * harmful if replayed after a reconnect (e.g. push-to-talk "request turn"). Returns whether it was sent.
   */
  sendNow(msg: object): boolean {
    if (!this.ws || this.state !== 'open') return false;
    this.ws.send(JSON.stringify(msg));
    return true;
  }

  on(type: string, fn: Listener): () => void {
    let set = this.listeners.get(type);
    if (!set) {
      set = new Set();
      this.listeners.set(type, set);
    }
    set.add(fn);
    return () => set!.delete(fn);
  }

  onState(fn: (s: SocketState) => void): () => void {
    this.stateListeners.add(fn);
    return () => this.stateListeners.delete(fn);
  }

  private emit(type: string, msg: any) {
    this.listeners.get(type)?.forEach((l) => l(msg));
  }

  close() {
    this.stopped = true;
    const ws = this.ws;
    this.ws = null;
    if (this.heartbeat) clearInterval(this.heartbeat);
    this.heartbeat = null;
    ws?.close();
    this.setState('closed');
  }
}

export function wsUrl(base = ''): string {
  if (base.startsWith('http')) return `${base.replace(/^http/, 'ws')}/ws`;
  const { protocol, host } = window.location;
  return `${protocol === 'https:' ? 'wss' : 'ws'}://${host}${base}/ws`;
}

/** Tenant hint for multi-tenant logins in environments without per-tenant domains (development). */
export function tenantHint(): string | null {
  const q = new URLSearchParams(window.location.search).get('tenant');
  if (q) {
    try {
      localStorage.setItem('desp.tenant', q);
    } catch {
      /* storage unavailable */
    }
    return q;
  }
  try {
    return localStorage.getItem('desp.tenant');
  } catch {
    return null;
  }
}

export function rememberTenant(slug: string) {
  try {
    localStorage.setItem('desp.tenant', slug);
  } catch {
    /* ignore */
  }
}
